import React, { useEffect, useRef } from 'react';
import Choices from 'choices.js';
import 'choices.js/public/assets/styles/choices.min.css';

/**
 * ChoiceSelect
 *
 * Optimized for stability in Modals.
 * Uses a single initialization and updates choices via setChoices.
 * Supports multiple selection via the `multiple` prop.
 */
const ChoiceSelect = ({
  name,
  value,
  onChange,
  options = [],
  placeholder = 'Select an option',
  required = false,
  searchEnabled = false,
  disabled = false,
  multiple = false,
}) => {
  const selectRef   = useRef(null);
  const choicesRef  = useRef(null);
  const onChangeRef = useRef(onChange);

  // Sync onChange prop to ref to avoid re-initializing Choices on every change
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // ── 1. Initialization ───────────────────────────────────────────────────
  useEffect(() => {
    const el = selectRef.current;
    if (!el) return;

    // Cleanup any existing instance (safety check)
    if (choicesRef.current) {
        choicesRef.current.destroy();
    }

    choicesRef.current = new Choices(el, {
      searchEnabled: multiple ? false : searchEnabled,
      itemSelectText: '',
      shouldSort: false,
      allowHTML: true,
      placeholder: !multiple,
      placeholderValue: placeholder,
      silent: true,
      removeItems: multiple,
      removeItemButton: multiple,
    });

    if (disabled) choicesRef.current.disable();

    const handleSelectChange = () => {
      if (onChangeRef.current) {
        const val = multiple
          ? Array.from(el.selectedOptions).map(o => o.value)
          : el.value;
        onChangeRef.current({ target: { name, value: val } });
      }
    };

    el.addEventListener('change', handleSelectChange);

    return () => {
      el.removeEventListener('change', handleSelectChange);
      if (choicesRef.current) {
        choicesRef.current.destroy();
        choicesRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchEnabled, placeholder, multiple]);
  // We only re-init if core behavioral props change.
  // options and value are handled by separate effects.

  // ── 2. Sync Options ──────────────────────────────────────────────────────
  useEffect(() => {
    const instance = choicesRef.current;
    if (!instance) return;

    const selectedStrings = multiple
      ? (Array.isArray(value) ? value.map(String) : (value ? [String(value)] : []))
      : null;

    // Convert options to Choices.js format
    const choiceList = options.map(opt => ({
      value: String(opt.value),
      label: opt.label,
      selected: multiple
        ? selectedStrings.includes(String(opt.value))
        : String(opt.value) === String(value),
      disabled: opt.disabled || false,
    }));

    // Add placeholder for single-select only
    if (!multiple && placeholder) {
        choiceList.unshift({
            value: '',
            label: placeholder,
            selected: !value,
            disabled: true,
            placeholder: true
        });
    }

    instance.clearStore();
    instance.setChoices(choiceList, 'value', 'label', true);
  }, [options, value, placeholder, multiple]);

  // ── 3. Sync Disabled State ──────────────────────────────────────────────
  useEffect(() => {
    const instance = choicesRef.current;
    if (!instance) return;
    if (disabled) instance.disable();
    else instance.enable();
  }, [disabled]);

  // ── 4. CSS Fixes for Visibility ─────────────────────────────────────────
  return (
    <div className="choice-select-container" style={{ position: 'relative', width: '100%', zIndex: 10 }}>
      <select ref={selectRef} name={name} required={required} multiple={multiple} />
      <style>{`
        .choices {
          margin-bottom: 0;
          font-size: 13px;
          color: #333;
        }
        .choices__inner {
          background-color: #fff !important;
          border: 1px solid #d1d1d1 !important;
          border-radius: 2px !important;
          min-height: 34px !important;
          height: auto !important;
          max-height: none !important;
          overflow: visible !important;
          padding: 4px 8px !important;
        }
        .choices--multiple .choices__inner {
          display: flex !important;
          flex-wrap: wrap !important;
          align-items: flex-start !important;
          gap: 4px !important;
          width: 100% !important;
          box-sizing: border-box !important;
        }
        .choices--multiple .choices__input {
          display: none !important;
        }
        .choices__list--multiple {
          display: flex !important;
          flex-wrap: wrap !important;
          gap: 4px !important;
          padding: 0 !important;
          margin: 0 !important;
        }
        .choices__list--multiple .choices__item {
          display: inline-flex !important;
          align-items: center !important;
          background-color: #e8f0fe !important;
          border: 1px solid #c5d5f5 !important;
          color: #1a2a4d !important;
          border-radius: 3px !important;
          padding: 2px 6px !important;
          font-size: 12px !important;
          font-weight: 500 !important;
          margin: 0 !important;
          white-space: nowrap !important;
        }
        .choices__list--multiple .choices__item .choices__button {
          background-color: #1a2a4d !important;
          border: none !important;
          border-left: 1px solid #c5d5f5 !important;
          cursor: pointer !important;
          padding: 0 0 0 5px !important;
          margin-left: 4px !important;
          opacity: 0.5 !important;
          width: 12px !important;
          height: 12px !important;
          flex-shrink: 0 !important;
        }
        .choices__list--multiple .choices__item .choices__button:hover {
          opacity: 1 !important;
        }
        .choices__list--dropdown {
          z-index: 10000 !important;
          background-color: #fff;
          border: 1px solid #ccc;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .choices__item--selectable.is-highlighted {
          background-color: #0078d4;
          color: #fff;
        }
      `}</style>
    </div>
  );
};

export default ChoiceSelect;
